














































































































































































































































































































































































import {
  computed,
  defineComponent,
  reactive,
  ref,
  getCurrentInstance
} from "@vue/composition-api";

import OccHeading from "@/components/Atoms/OccHeading/index.vue";
import OccLabel from "@/components/Atoms/OccLabel/index.vue";
import OccButton from "@/components/Atoms/OccButton/index.vue";
import FieldErrorMessage from "@/components/Atoms/FieldErrorMessage/index.vue";
import Radio from "@/components/Atoms/Forms/Radio/index.vue";
import ToggleContent from "@/components/Atoms/ToggleContent/index.vue";
import OccSelectboxDate from "@/components/Atoms/OccSelectboxDate/index.vue";
import TextArea from "@/components/Atoms/Forms/TextArea/index.vue";
import VersionSwitchAlertModal from "../../Organisms/modals/VersionSwitchAlertModal/index.vue";
import ConfirmAlertModal from "../../Organisms/modals/ConfirmAlertModal/index.vue";
import ConfirmModal from "@/components/Organisms/modals/ConfirmModal/index.vue";
import Caption from "@/components/Atoms/Typography/Caption/index.vue";
import { sendGaEvent } from "@/helpers/ga-helper";

import {
  getDisplayPublicStatus,
  getApprovalPublicStatus,
  PreferredDateType
} from "@/helpers/official";
import { OFFICIAL_STORE_KEY } from "@/helpers/hooks/useOfficial";
import { ApiResponse } from "@/api/types";
import { occWhite } from "@/assets/styles/vars-for-script.scss";
import dayjs from "dayjs";
import { getBusinessDate } from "@/helpers/date-helper";
import { useGlobalState } from "@/helpers/hooks/useGlobalState";

export default defineComponent({
  components: {
    OccHeading,
    OccLabel,
    OccButton,
    FieldErrorMessage,
    Radio,
    ToggleContent,
    OccSelectboxDate,
    TextArea,
    VersionSwitchAlertModal,
    ConfirmAlertModal,
    ConfirmModal,
    Caption
  },
  name: "OfficialSidebar",
  props: {
    isNewOfficial: {
      type: Boolean,
      default: true
    },
    gaEventCategory: {
      type: String,
      default: ""
    },
    companyLogoUrl: {
      type: String,
      default: ""
    }
  },
  emits: ["preview", "save-draft", "confirm", "unconfirm", "checkCompanyLogo"],
  setup(props, { emit }) {
    /** ====================================================
     * ストア
     ==================================================== */
    const {
      isOfficialEdited,
      officialsData,
      officialDetailData,
      officialEditingForm,
      isPendingVersion,
      onSetForm,
      getOfficialErrorMessage,
      validateForDraft,
      validateForSubmit,
      hasOfficialErrors,
      resetAllOfficialError,
      switchOfficialVersion
    } = useGlobalState(OFFICIAL_STORE_KEY);

    /** ====================================================
     * バージョン切り替え
     ==================================================== */
    const displayVersionList = computed(() => {
      if (!officialsData.value || !officialsData.value.officials) return [];
      return officialsData.value.officials.reverse();
    });
    const selectedVersion = ref<ApiResponse.Official>();

    const instance = getCurrentInstance();
    const onSelectVersion = (official: ApiResponse.Official) => {
      if (instance) {
        sendGaEvent(instance.proxy, {
          gaEventCategory: `右サイドバー_バージョン_${official.version}`
        });
      }
      // 編集している場合、変更破棄して良いか警告するモーダルを出す
      if (isOfficialEdited.value) {
        selectedVersion.value = official;
        showModal("versionSwitchAlertModal");
      } else {
        switchOfficialVersion(official.id);
      }
    };

    const applySwitchVersion = () => {
      closeModal("versionSwitchAlertModal");
      if (selectedVersion.value) {
        switchOfficialVersion(selectedVersion.value.id);
      }
    };

    const isEditingVersion = (official: ApiResponse.Official) => {
      return official.id === officialDetailData.value?.id;
    };

    /** ====================================================
     * 公開・更新日時
     ==================================================== */
    const onChangeSubmitType = (value: PreferredDateType) => {
      onSetForm({ mainKey: "submit_type" }, value);

      // 日時指定公開の場合デフォルトの日時を入れる
      if (value === "specifiedDate") {
        if (!officialEditingForm.value.start_at) {
          // 指定された日時がない場合、設定できる最短の値（3営業日後）を入れる
          const minimumDate = getBusinessDate(4).format("YYYY/MM/DD HH:mm");
          onSetForm({ mainKey: "start_at" }, minimumDate);
        }
      } else {
        // 即時公開の場合の日時をnullにする
        onSetForm({ mainKey: "start_at" }, "");
      }
    };

    const onChangePreferredPublishAt = (publishAt: string) => {
      if (!officialEditingForm.value) return;

      onSetForm({ mainKey: "start_at" }, publishAt);
    };

    /** ====================================================
     * ボタンクリック（プレビュー・下書き保存・掲載依頼）
     ==================================================== */
    const onClickPreview = async () => {
      emit("preview");
    };

    const onClickSaveDraft = async () => {
      resetAllOfficialError();

      // 下書き保存に必要なバリデーションが通るかチェック
      await validateForDraft();
      if (hasOfficialErrors.value) return;

      emit("save-draft");
    };

    const onClickConfirm = async () => {
      resetAllOfficialError();

      await validateForSubmit();
      if (hasOfficialErrors.value) return;

      if (!props.companyLogoUrl || props.companyLogoUrl === "") {
        emit("checkCompanyLogo");
        return;
      }
      showModal("confirmAlertModal");
    };

    const confirm = async () => {
      closeModal("confirmAlertModal");
      emit("confirm");
    };

    const unconfirm = async () => {
      closeModal("unconfirmAlertModal");
      emit("unconfirm");
    };

    const modalShowStatus = reactive({
      // バージョン切り替え 確認モーダル
      versionSwitchAlertModal: false,
      // 掲載依頼 確認モーダル
      confirmAlertModal: false,
      // 掲載依頼取り消し 確認モーダル
      unconfirmAlertModal: false
    });

    const showModal = (modalKey: keyof typeof modalShowStatus) => {
      modalShowStatus[modalKey] = true;
    };

    const closeModal = (modalKey: keyof typeof modalShowStatus) => {
      modalShowStatus[modalKey] = false;
    };

    /** ====================================================
     * スタイル/フォーマット
     ==================================================== */
    const formatDate = (at: string, hideTime: boolean = false) =>
      dayjs(new Date(at)).format(!hideTime ? "YYYY/MM/DD HH:mm" : "YYYY/MM/DD");

    const getDisplayDate = (officialDetailData: ApiResponse.OfficialDetail) => {
      // 日時指定の場合
      if (officialDetailData.start_at) {
        switch (officialDetailData.public_status) {
          case "private":
          case "coming_soon":
            return {
              approved_date: undefined,
              published_date: undefined
            };
          case "public":
          case "ended": {
            // 承認日時の方が希望公開日時より後の場合、公開日時 = 承認日時
            const publishedAtTime = new Date(
              officialDetailData.publish_at
            ).getTime();
            const startAtTime = new Date(officialDetailData.start_at).getTime();

            return {
              approved_date: formatDate(officialDetailData.publish_at),
              published_date: formatDate(
                publishedAtTime > startAtTime
                  ? officialDetailData.publish_at
                  : officialDetailData.start_at
              )
            };
          }
          case "suspend":
            return {
              approved_date: formatDate(officialDetailData.publish_at),
              published_date: undefined
            };
        }
      }
      // 承認後すぐ公開の場合
      else {
        return {
          approved_date: officialDetailData.publish_at
            ? formatDate(officialDetailData.publish_at)
            : undefined,
          published_date: officialDetailData.publish_at
            ? formatDate(officialDetailData.publish_at)
            : undefined
        };
      }
    };

    const getVersionStatusColor = (
      officialDetailData: ApiResponse.Official
    ) => {
      if (officialDetailData.public_status === "private") {
        // 公開ステータスが「非公開」の場合、承認ステータスを表示するため
        // 承認ステータスに応じて色を変える
        switch (officialDetailData.approval_status) {
          case "rejected":
            return "alert";
          default:
            return "gray";
        }
      } else {
        switch (officialDetailData.public_status) {
          case "ended":
            return "gray";
          case "public":
          case "coming_soon":
            return "primary";
          default:
            return "gray";
        }
      }
    };

    const getDisplayVersionStatus = (
      officialDetailData: ApiResponse.Official
    ) => {
      return officialDetailData.public_status !== "private"
        ? getDisplayPublicStatus(officialDetailData.public_status)
        : getApprovalPublicStatus(officialDetailData.approval_status);
    };

    return {
      getDisplayPublicStatus,
      getApprovalPublicStatus,

      occWhite,

      officialsData,
      officialDetailData,
      officialEditingForm,
      isPendingVersion,
      onSetForm,
      getOfficialErrorMessage,

      displayVersionList,
      selectedVersion,
      onSelectVersion,
      applySwitchVersion,
      isEditingVersion,

      onChangeSubmitType,
      onChangePreferredPublishAt,

      onClickPreview,
      onClickSaveDraft,
      onClickConfirm,
      confirm,
      unconfirm,

      modalShowStatus,
      showModal,
      closeModal,

      formatDate,
      getDisplayDate,
      getVersionStatusColor,
      getDisplayVersionStatus
    };
  }
});
